import { Box, chakra, ChakraProps, Flex, Grid, GridItem, Text, useBoolean } from "@chakra-ui/react";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import PayModal, { CircleCheck } from "../components/PayModal";
import { getAccelerateProducts, getHeartCoinProducts } from "../request";
import PriceCard from "../components/PriceCard";
import { ObjKeysCamelFormat } from "@/utils/variableName";
import QRcodeCard from "../components/QrcodeCard";
import { observer } from "mobx-react-lite";
import config from "@/config";
import WenXinPaySuccessModal from "../components/HeartCoinPaySuccessModal";
import { genResponsivePx } from "@/utils/style";
import { userAssetsStore } from "@/stores/userAssets";
import { accelerationStore } from "@/stores/acceleration";
import AcceleratePayment from "../AcceleratePayment";

interface ProductInfoItem {
  activeDesc: string;
  activePrice: string;
  createdAt: string;
  id: number;
  name: string;
  wenxin: number;
  price: string;
  unitPrice: string;
}

interface VipProductInfoItem {
  activeDesc: string;
  activePrice: string;
  createdAt: string;
  id: number;
  name: string;
  period: number;
  price: string;
  unitPrice: string;
  isUsed: boolean;
}

interface ProductInfoData<T = ProductInfoItem> {
  vipMonthPrice: string;
  vipUnitPrice: string;
  productInfo: T[];
}

const titleStyle: ChakraProps = {
  fontSize: "24px",
  pt: "48px",
  pb: "32px",
  textAlign: "center",
  fontWeight: 600,
};
const goldGradient = `linear-gradient(90deg, #E6A441, #FAD998);`;

const _HeartCoinPayment: React.FC = () => {
  const {
    heartCoinPayModalOpen: open,
    closeHeartCoinPayModal: handleClose,
    onHeartCoinPaySuccess,
  } = userAssetsStore;
  const { isVip } = accelerationStore;

  const [accelerateOpen, setAccelerateOpen] = useBoolean(false);
  const descList: Array<string | ReactNode> = useMemo(() => {
    return [
      "付费购得的画力星可通用于画宇宙系列模型功能和第三方模型功能（百度文心、Draft 动漫模型、社区大模型等）",
      "非会员用户每日登录，可获赠 30 免费画力星，仅可用于使用画宇宙系列模型功能（不支持第三方模型功能）",
      "会员用户可无限量使用画宇宙系列模型功能，会员用户使用第三方模型功能时仍会扣除画力星",
      <Flex key="4" pos="relative" mb="16px">
        <CircleCheck w="20px" h="20px" pos="absolute" left={0} top={0} />
        <Text fontSize="14px" ml="40px" pr="20px" lineHeight="20px">
          推荐您成为{" "}
          <chakra.span
            color="brand.primary"
            onClick={() => {
              handleClose();
              setAccelerateOpen.on();
            }}
          >
            画宇宙会员
          </chakra.span>{" "}
          ，会员用户购买画力星尊享优惠价，且可购买任意整百数量画力星
        </Text>
      </Flex>,
      <Flex key="5" pos="relative">
        <CircleCheck w="20px" h="20px" pos="absolute" left={0} top={0} />
        <Text fontSize="14px" ml="40px" pr="20px" lineHeight="20px">
          我们推荐您尝试{" "}
          <chakra.span
            as="a"
            color="brand.primary"
            target="_blank"
            href="https://f8y4v1rln8.feishu.cn/wiki/wikcnIbD4EhOkuk4baag1T21cX9"
          >
            本地部署方案
          </chakra.span>{" "}
          ，使用自己的电脑算力，画宇宙核心功能免费使用
        </Text>
      </Flex>,
    ];
  }, [setAccelerateOpen.on, handleClose]);

  const [paidModalOpen, setPaidModalOpen] = useState(false);

  const [products, setProducts] = useState<ProductInfoItem[]>([]);
  const [customProduct, setCustomProduct] = useState<ProductInfoItem | null>(null);
  const [customPrice, setCustomPrice] = useState<null | {
    vipMonthPrice: string;
    vipUnitPrice: string;
  }>(null);
  const [buyCustomCount, setBuyCustomCount] = useState(1000);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoItem | null>(null);
  const [defaultVipProduct, setDefaultVipProduct] = useState<VipProductInfoItem | null>(null);

  useEffect(() => {
    getAccelerateProducts()
      .then((res) => res)
      .then((res) => {
        const data = ObjKeysCamelFormat(res.data) as ProductInfoData<VipProductInfoItem>;
        for (let i = 0; i < data.productInfo.length; i++) {
          const p = data.productInfo[i];
          if ((p.period === 1 || p.period === 2) && !p.isUsed) {
            setDefaultVipProduct(p);
            break;
          } else if (p.period === 30 || p.period === 31) {
            setDefaultVipProduct(p);
            break;
          }
        }
      });
  }, []);

  useEffect(() => {
    getHeartCoinProducts()
      .then((res) => res)
      .then((res) => {
        if (res.code === 0) {
          const data = ObjKeysCamelFormat(res.data) as ProductInfoData;
          let customProduct: ProductInfoItem | null = null;
          const products: ProductInfoItem[] = [];
          const { vipMonthPrice, vipUnitPrice, productInfo } = data;
          productInfo.forEach((product) => {
            if (product.id === 10000) {
              customProduct = product;
            } else {
              products.push(product);
            }
          });
          if (customProduct) {
            setCustomProduct(customProduct);
            setSelectedProduct(customProduct);
          } else {
            setSelectedProduct(products[0]);
          }
          setProducts(products);
          setCustomPrice({
            vipMonthPrice,
            vipUnitPrice,
          });
        }
      });
  }, [isVip]);

  const redirectURI = useMemo(() => {
    return `${config.creatorMobileURL}/mobile_payment?type=wenxin&coins=${
      selectedProduct?.id === 10000 ? buyCustomCount : selectedProduct?.wenxin
    }`;
  }, [selectedProduct, buyCustomCount]);

  const qrAmount = useMemo(() => {
    if (selectedProduct?.id === 10000) {
      const vipPrice = isVip ? 0 : Number(defaultVipProduct?.activePrice);
      return Number(customPrice?.vipUnitPrice) * buyCustomCount + vipPrice;
    } else {
      return Number(selectedProduct?.activePrice);
    }
  }, [selectedProduct, isVip, buyCustomCount, defaultVipProduct]);

  return (
    <>
      <PayModal
        isOpen={open}
        rightTitle={
          <Text {...titleStyle}>
            <chakra.span bg={goldGradient} bgClip="text">
              购买「画力星
            </chakra.span>
            ⭐
            <chakra.span bg={goldGradient} bgClip="text">
              」
            </chakra.span>
          </Text>
        }
        onClose={handleClose}
        leftTitle="付费说明"
        // rightTitle="购买「画力星 ⭐」"
        descList={descList}
        rightContent={
          <Flex flexDirection="column">
            <Grid w="100%" gap="16px" templateColumns="repeat(2, 1fr)">
              {products.map((product, index) => {
                return (
                  <GridItem key={index}>
                    <Box w="222px">
                      <PriceCard
                        title={product.name}
                        oldPrice={product.price}
                        curPrice={product.activePrice}
                        unitPrice={product.unitPrice}
                        selected={selectedProduct?.id === product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          setBuyCustomCount(product.wenxin);
                        }}
                      />
                    </Box>
                  </GridItem>
                );
              })}
              {customProduct && (
                <GridItem>
                  <Box w="222px">
                    <PriceCard
                      title={customProduct.name}
                      oldPrice={customProduct.price}
                      curPrice={customProduct.activePrice}
                      unitPrice={customPrice?.vipUnitPrice}
                      selected={selectedProduct?.id === customProduct.id}
                      onClick={() => setSelectedProduct(customProduct)}
                      custom
                      customVal={buyCustomCount}
                      onChange={setBuyCustomCount}
                    />
                  </Box>
                </GridItem>
              )}
            </Grid>
            <Text m="16px 0" fontSize="12px" lineHeight="22px" color="#999999"></Text>
            {selectedProduct ? (
              <QRcodeCard
                productId={selectedProduct?.id}
                amount={qrAmount}
                amountDesc={
                  isVip || selectedProduct.id !== 10000
                    ? ""
                    : `（含${defaultVipProduct?.activePrice}元${
                        Number(defaultVipProduct?.period) / 2 <= 1 ? "日" : "月"
                      }会员费）`
                }
                heartCoin={buyCustomCount}
                isYuan
                onSuccess={() => {
                  setPaidModalOpen(true);
                  accelerationStore.afterPayment();
                  onHeartCoinPaySuccess();
                }}
                redirectUri={redirectURI}
              />
            ) : null}
          </Flex>
        }
      />
      <WenXinPaySuccessModal
        isOpen={paidModalOpen}
        onClose={() => setPaidModalOpen(false)}
        title="支付成功"
        footerText="使用中有任何问题可通过微信联系客服"
        onBtnClick={() => setPaidModalOpen(false)}
        desc={
          <Text fontSize={genResponsivePx(24)}>
            <chakra.span
              fontSize={genResponsivePx(40)}
              lineHeight={genResponsivePx(56)}
              fontWeight="600"
            >
              {selectedProduct?.id === 10000 ? buyCustomCount : selectedProduct?.wenxin ?? ""}
            </chakra.span>{" "}
            <chakra.span lineHeight={genResponsivePx(33.6)}>画力星⭐已加入账号</chakra.span>
          </Text>
        }
      />
      <AcceleratePayment open={accelerateOpen} onClose={setAccelerateOpen.off} />
    </>
  );
};

export const HeartCoinPayment = observer(_HeartCoinPayment);
